import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api/client";
import {
  getAdoPatErrorCode,
  workItemsApi,
  type AuthorQuestionsDraft,
  type AuthorQuestionsResponse,
  type GroomingDisregardUndo,
  type LatestAuthorQuestionAsk,
  type MyWorkItemsFilters,
  type TrackerKind,
  type WorkItemAddress,
  type WorkItemDetail,
} from "@/lib/api/work-items";
import type { GroomingBacklog, GroomingBacklogItem } from "@/lib/api/grooming";

/**
 * Root of every grooming query key. Defined here, not in use-grooming.ts,
 * because use-grooming.ts imports this file: the unskip hook below refreshes
 * the grooming caches without an import cycle. `groomingKeys.all` is this.
 */
export const groomingRootKey = ["grooming"] as const;

/** One project's grooming reads (backlog, session, batches, config); `groomingKeys.project` is this. */
export function groomingProjectKey(project: string) {
  return [...groomingRootKey, "project", project.toLowerCase()] as const;
}

/** Every batch read; `groomingKeys.batch(id)` sits under it. */
export const groomingBatchRootKey = [...groomingRootKey, "batch"] as const;

/** Query key factory for the work-item API (PI-SPEC-SW-1362). */
export const workItemKeys = {
  all: ["work-items"] as const,
  mine: (filters: MyWorkItemsFilters = {}) =>
    [
      ...workItemKeys.all,
      "mine",
      { projects: [...(filters.projects ?? [])].sort(), kind: filters.kind ?? null },
    ] as const,
  details: () => [...workItemKeys.all, "detail"] as const,
  detail: (tracker: TrackerKind, container: string, id: string) =>
    [...workItemKeys.details(), tracker, container.toLowerCase(), id] as const,
  projects: () => [...workItemKeys.all, "projects"] as const,
  /** Drafted author questions of an item (E280); under the item key, so refreshing the item refreshes them. */
  authorQuestionsDraft: (tracker: TrackerKind, container: string, id: string) =>
    [...workItemKeys.detail(tracker, container, id), "author-questions-draft"] as const,
};

/**
 * Retry a transient failure (network, 5xx, 502 from ADO) once. Never retry a
 * client error: a rejected or missing PAT, a missing item or a throttled
 * account will not fix itself, and each retry spends the user's ADO rate budget.
 */
export function retryTransientOnce(failureCount: number, error: unknown): boolean {
  if (getAdoPatErrorCode(error)) return false;
  if (error instanceof ApiError && error.response.status < 500) return false;
  return failureCount < 1;
}

/** The caller's assigned bugs and stories with live readiness. */
export function useMyWorkItems(filters: MyWorkItemsFilters = {}) {
  return useQuery({
    queryKey: workItemKeys.mine(filters),
    queryFn: () => workItemsApi.mine(filters),
    select: (response) => response.data,
    retry: retryTransientOnce,
  });
}

/**
 * One item with live readiness. `rideAlong` is for a second reader of an item
 * another component already shows (the brief state bar, E289): it uses the
 * cached read instead of making a new live ADO read when it mounts, and still
 * reads the item itself when nothing has. `staleTime` shortens the app-wide
 * 60 s for a reader that must not show an older read when it mounts (Present
 * mode's slides, PI-SPEC-SW-1488).
 */
export function useWorkItem(
  tracker: TrackerKind,
  container: string | undefined,
  id: string | undefined,
  options: { rideAlong?: boolean; staleTime?: number; enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: workItemKeys.detail(tracker, container ?? "", id ?? ""),
    queryFn: () => workItemsApi.detail(tracker, container ?? "", id ?? ""),
    select: (response) => response.data,
    enabled: Boolean(container && id) && options.enabled !== false,
    retry: retryTransientOnce,
    ...(options.rideAlong ? { refetchOnMount: false } : {}),
    ...(options.staleTime !== undefined ? { staleTime: options.staleTime } : {}),
  });
}

/*
 * The item page reads its grooming brief from the item query
 * (`WorkItemDetail.groomingBrief`, ER-1603 fix B): one request, one ADO read.
 * There is deliberately no hook for `.../grooming-briefs/latest`; that route
 * stays for MCP and skill callers.
 */

/** Refresh an item (and its nested keys) after a brief changed on the server. */
function invalidateItem(queryClient: ReturnType<typeof useQueryClient>, item: WorkItemAddress): void {
  void queryClient.invalidateQueries({ queryKey: workItemKeys.detail(item.tracker, item.container, item.id) });
}

/**
 * Write a brief's new comment status into the project's cached backlog rows
 * (E289), so the run list and the Agenda, which only read the backlog from
 * the cache (`useBacklogBriefs`), show a Post, Hold back or Undo without a
 * new ADO read.
 */
function patchCachedBacklogBrief(
  queryClient: ReturnType<typeof useQueryClient>,
  project: string,
  briefId: string,
  commentStatus: string,
  extra: Partial<Pick<GroomingBacklogItem, "hasCurrentBrief">> = {}
): void {
  queryClient.setQueriesData<{ data: GroomingBacklog }>({ queryKey: [...groomingProjectKey(project), "backlog"] }, (entry) => {
    if (!entry) return entry;
    const patch = (row: GroomingBacklogItem): GroomingBacklogItem =>
      row.aiSuggestion?.briefId === briefId ? { ...row, ...extra, aiSuggestion: { ...row.aiSuggestion, commentStatus } } : row;
    return { ...entry, data: { ...entry.data, items: entry.data.items.map(patch), exceptions: entry.data.exceptions.map(patch) } };
  });
}

/** The project reads that show a brief's state: the backlog state line, the Session badge and agenda, the batch list. */
const BRIEF_STATE_READS = ["backlog", "session", "batches"] as const;

/**
 * Refresh what shows a brief's state besides the item (epic PI-SPEC-SW-E289,
 * UX-PM-02), wherever the brief was posted or held back from: the cached
 * backlog rows take the new comment status, then the project's backlog,
 * session and batch list, and every batch read (the run list and Post all
 * (N)), are marked out of date. Only reads a page shows refetch now
 * (`refetchType: "active"`): a page that holds the whole backlog only from
 * the cache starts no backlog read.
 */
function invalidateBriefState(
  queryClient: ReturnType<typeof useQueryClient>,
  item: WorkItemAddress,
  brief: { id: string; commentStatus: string }
): void {
  invalidateItem(queryClient, item);
  patchCachedBacklogBrief(queryClient, item.container, brief.id, brief.commentStatus);
  for (const read of BRIEF_STATE_READS) {
    void queryClient.invalidateQueries({ queryKey: [...groomingProjectKey(item.container), read], refetchType: "active" });
  }
  void queryClient.invalidateQueries({ queryKey: groomingBatchRootKey });
}

/** Post a held (or failed) grooming brief to ADO as the signed-in user (E279). */
export function usePostHeldBrief(item: WorkItemAddress) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ briefId, force }: { briefId: string; force?: boolean }) =>
      workItemsApi.postHeldBrief(item, briefId, force ? { force } : {}).then((response) => response.data),
    // Posting reaches the team: never retried automatically (E279).
    retry: false,
    onSuccess: (result) => {
      invalidateBriefState(queryClient, item, { id: result.briefId, commentStatus: result.commentStatus });
    },
  });
}

/** Skip a held grooming brief (E279; Hold back, with or without a run since E289). */
export function useSkipHeldBrief(item: WorkItemAddress) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (briefId: string) => workItemsApi.skipHeldBrief(item, briefId).then((response) => response.data),
    retry: false,
    onSuccess: (result) => {
      invalidateBriefState(queryClient, item, { id: result.briefId, commentStatus: "skipped" });
    },
  });
}

/**
 * Undo a skip (E280, Hold back's Undo): the brief is held again and its batch
 * item back to review state `pending`. Refreshes the item and every grooming
 * read (session, batches, backlog). 409 `GROOMING_BRIEF_NOT_SKIPPED` once
 * posted. Never retried.
 */
export function useUnskipBrief(item: WorkItemAddress) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (briefId: string) => workItemsApi.unskipHeldBrief(item, briefId).then((response) => response.data),
    retry: false,
    onSuccess: (result) => {
      invalidateItem(queryClient, item);
      patchCachedBacklogBrief(queryClient, item.container, result.briefId, "held");
      void queryClient.invalidateQueries({ queryKey: groomingRootKey });
    },
  });
}

/**
 * Disregard an unposted (held, skipped or failed) brief (grooming contract 5,
 * ER-1712): the brief is discarded and its batch items go back to "needs
 * brief", so the agenda lists the item again and Prepare briefs counts it.
 * Refreshes the item and every grooming read (session, batches, backlog,
 * shared agenda). The result's `undo` is kept by the caller in memory only.
 * Never retried.
 */
export function useDisregardBrief(item: WorkItemAddress) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (briefId: string) => workItemsApi.disregardBrief(item, briefId).then((response) => response.data),
    retry: false,
    onSuccess: (result) => {
      invalidateItem(queryClient, item);
      // No current brief until the backlog read comes back: the agenda counts the item for Prepare briefs at once.
      patchCachedBacklogBrief(queryClient, item.container, result.briefId, result.commentStatus, { hasCurrentBrief: false });
      void queryClient.invalidateQueries({ queryKey: groomingRootKey });
    },
  });
}

/**
 * Undo a disregard with the `undo` token the disregard returned. Refreshes the
 * item and every grooming read, like {@link useUnskipBrief}. 409
 * `GROOMING_DISREGARD_UNDO_UNAVAILABLE` once a newer brief exists or the item
 * moved on. Never retried.
 */
export function useUndoDisregardBrief(item: WorkItemAddress) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ briefId, undo }: { briefId: string; undo: GroomingDisregardUndo }) =>
      workItemsApi.undoDisregardBrief(item, briefId, undo).then((response) => response.data),
    retry: false,
    onSuccess: (result) => {
      invalidateItem(queryClient, item);
      patchCachedBacklogBrief(queryClient, item.container, result.briefId, result.commentStatus);
      void queryClient.invalidateQueries({ queryKey: groomingRootKey });
    },
  });
}

/**
 * The latest drafted questions for an item's author (E280), or null when none
 * were drafted (the API's 404). `isCurrent` turns false once the item changed.
 * Reads the item live with the caller's PAT, so it is never retried on a
 * client error. `cacheOnly` (E289, the Queue's result cards) only follows a
 * read another component made and never starts one.
 */
export function useAuthorQuestionsDraft(
  project: string | undefined,
  itemId: string | undefined,
  options: { cacheOnly?: boolean } = {}
) {
  return useQuery({
    queryKey: workItemKeys.authorQuestionsDraft("ado", project ?? "", itemId ?? ""),
    queryFn: async (): Promise<AuthorQuestionsDraft | null> => {
      try {
        return (await workItemsApi.getAuthorQuestionsDraft({ tracker: "ado", container: project ?? "", id: itemId ?? "" }))
          .data;
      } catch (error) {
        if (error instanceof ApiError && error.response.status === 404) return null;
        throw error;
      }
    },
    enabled: !options.cacheOnly && Boolean(project && itemId),
    retry: retryTransientOnce,
  });
}

/**
 * Store drafted questions for an item's author (E280). The Toolbox main
 * process is the normal writer; this is the browser fallback. Refreshes the
 * draft, the item and the grooming reads. Never retried.
 */
export function useSaveAuthorQuestionsDraft(item: WorkItemAddress) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questions: string[]) =>
      workItemsApi.saveAuthorQuestionsDraft(item, questions).then((response) => response.data),
    retry: false,
    onSuccess: () => {
      invalidateItem(queryClient, item);
      void queryClient.invalidateQueries({ queryKey: groomingRootKey });
    },
  });
}

/**
 * The newest ask of an item's author, its count and whether the item changed
 * since (E292, UX-PM-06), read from the item read (`WorkItemDetail.authorAsk`,
 * ER-1603 fix B): no request and no ADO read of its own. `data` is null when
 * the author was never asked and undefined while unknown; `isError` when the
 * item read failed or SpecTree could not read the item's asks.
 */
export function useAuthorQuestionsLatestAsk(project: string | undefined, itemId: string | undefined) {
  const query = useWorkItem("ado", project, itemId, { rideAlong: true });
  const unknown = query.data !== undefined && query.data.authorAsk === undefined;
  return {
    data: query.data === undefined || unknown ? undefined : (query.data.authorAsk ?? null),
    isLoading: query.isLoading,
    isError: query.isError || unknown,
    refetch: () => query.refetch(),
  };
}

/**
 * The latest ask a post just stored, ahead of the re-read: one more ask, and
 * the item unchanged since (it was just read to post).
 */
export function latestAskAfterPost(
  result: AuthorQuestionsResponse,
  previous: LatestAuthorQuestionAsk | null | undefined
): LatestAuthorQuestionAsk | null {
  if (!result.ask) return previous ?? null;
  return { ...result.ask, askCount: (previous?.askCount ?? 0) + 1, changedSinceAsk: false, itemChangedAt: null };
}

/**
 * Post questions for the author of an item below the grooming bar (E279).
 * Since E292 each post is stored as an ask: the latest ask takes the stored
 * one at once and is re-read, and the item and the project's backlog (whose
 * rows say who was asked) refresh. Posting reaches the author: never retried.
 */
export function usePostAuthorQuestions(item: WorkItemAddress) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (questions: string[]) =>
      workItemsApi.postAuthorQuestions(item, questions).then((response) => response.data),
    retry: false,
    onSuccess: (result) => {
      // The item read carries the latest ask: show the stored one at once, then re-read the item.
      queryClient.setQueryData<{ data: WorkItemDetail }>(
        workItemKeys.detail(item.tracker, item.container, item.id),
        (previous) =>
          previous && { ...previous, data: { ...previous.data, authorAsk: latestAskAfterPost(result, previous.data.authorAsk) } }
      );
      invalidateItem(queryClient, item);
      void queryClient.invalidateQueries({ queryKey: [...groomingProjectKey(item.container), "backlog"] });
    },
  });
}

/** Projects My Items covers (Agile-family). */
export function useWorkItemProjects() {
  return useQuery({
    queryKey: workItemKeys.projects(),
    queryFn: () => workItemsApi.projects(),
    select: (response) => response.data,
    retry: retryTransientOnce,
    staleTime: 10 * 60 * 1000,
  });
}
